import { useAuth0 } from "@auth0/auth0-react";

import { CircularProgress, IconButton, Tooltip, styled, useTheme } from "@mui/material";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { FileArrowDownIcon } from "@phosphor-icons/react/dist/csr/FileArrowDown";
import * as Sentry from "@sentry/react";
import { subDays } from "date-fns";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { validateAuth0ApiErrors } from "../../context/utilsAuth0";
import { downloadFileApiCall } from "../../networking";
import { requireToken } from "../../strict/lib/authErrors";
import swallowClick from "../SwallowClick";
import { CoreIcon } from "../common/StatusIcon";

const CoreIconStyled = styled(CoreIcon)`
  & {
    font-size: 1em;
  }
`;

const DownloadIconContainer = styled("div")`
  display: inline-block;
`;

const ErrorIcon = ({ errorMessage = "Unknown error", onDownload }: Omit<IconType, "mode">) => (
  <Tooltip
    placement="left"
    id={"DownloadSamplesTooltip"}
    title={`There was an error downloading samples: ${errorMessage}. Click to Retry.`}
    data-cy="error-icon-tooltip"
  >
    <DownloadIconContainer>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          onDownload();
        }}
      >
        <CoreIconStyled type={"error"} />
      </IconButton>
    </DownloadIconContainer>
  </Tooltip>
);

interface IconType {
  mode: string | true;
  errorMessage?: string;
  onDownload: () => void;
}

const ExpiredIcon: FC<IconType> = () => {
  const theme = useTheme();
  return (
    <Tooltip id={"DownloadSamplesTooltip"} title="Samples are expired after 30 days." placement="left">
      <DownloadIconContainer>
        <FileArrowDownIcon color={theme.palette.text20.main} data-cy="expired-icon" />
      </DownloadIconContainer>
    </Tooltip>
  );
};

const LoadingIcon: FC<IconType> = () => (
  <Tooltip
    id={"DownloadSamplesTooltip"}
    title="Samples are downloading."
    placement="left"
    data-cy="loading-icon-tooltip"
  >
    <span>
      <IconButton disabled>
        <CircularProgress size="18px" />
      </IconButton>
    </span>
  </Tooltip>
);

const ReadyIconBase = ({ onDownload }: Pick<IconType, "onDownload">) => (
  <Tooltip id={"DownloadSamplesTooltip"} placement="left" title="Click to Download Samples.">
    <IconButton onClick={onDownload}>
      <DownloadSimpleIcon data-testid="download-icon" />
    </IconButton>
  </Tooltip>
);
const ReadyIcon = swallowClick(ReadyIconBase);

interface DownloadSamplesIconProps {
  flow_source_id: number | string;
  initialMode?: string;
  finished?: string | null;
}

/**
 * @deprecated This component is deprecated and will be removed in future releases. We should use DownloadSamplesButton instead. // TODO Remove (#21394 or #21412)
 */
const DownloadSamplesIcon: FC<DownloadSamplesIconProps> = ({ flow_source_id, finished, initialMode = "" }) => {
  const expiredDate = subDays(new Date(), 30);
  const isExpired = Boolean(finished && expiredDate > new Date(finished));
  const [mode, setMode] = useState(isExpired ? "expired" : initialMode || "ready");
  const [prevFinished, setPrevFinished] = useState(finished);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  if (prevFinished !== finished) {
    setPrevFinished(finished);
    const newExpiredDate = subDays(new Date(), 30);
    if (finished && newExpiredDate > new Date(finished)) {
      setMode("expired");
    }
  }

  const downloadFile = async () => {
    setMode("loading");
    let link: HTMLAnchorElement | null = null;
    const accessToken = requireToken(await getAccessTokenSilently());
    downloadFileApiCall(flow_source_id, accessToken)
      .then((response) => {
        validateAuth0ApiErrors(response, navigate);
        // Success
        setMode("ready");
        const url = window.URL.createObjectURL(new Blob([response.data]));
        link = document.createElement("a");
        link.href = url;
        const filename =
          response.headers["content-disposition"]?.split("filename=")[1] || new Date().toISOString() + "_samples.txt";
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      })
      .catch(function (error) {
        Sentry.captureException(error);

        // Error
        const reader = new FileReader();
        reader.onload = function () {
          // we ensure that result is indeed string type
          const result: string = this.result ? this.result.toString() : "";
          const error = JSON.parse(result);
          setMode("error");
          setError(error.error || error.message);
        };
        reader.readAsText(new Blob([error.response.data], { type: "application/json" }));
      })
      .then(function () {
        // Always Runs
        if (link) {
          link.parentNode?.removeChild(link);
        }
      });
  };

  const iconProps = { mode, errorMessage: error, onDownload: downloadFile };
  if (mode === "ready") {
    return <ReadyIcon {...iconProps} />;
  } else if (mode === "loading") {
    return <LoadingIcon {...iconProps} />;
  } else if (mode === "error") {
    return <ErrorIcon {...iconProps} />;
  } else if (mode === "expired") {
    return <ExpiredIcon {...iconProps} />;
  } else {
    return null;
  }
};

export { DownloadSamplesIcon };
